/* Product quiz — sections/product-quiz.liquid
   Multi-step recommendation quiz. All result cards are rendered server-side in
   Liquid (so prices come from the store, never hand-formatted); this script
   only moves between panels, tallies answer tags and reveals the winning
   result card. Scoring: each answer carries comma-separated tags; the result
   block whose tags overlap the accumulated tags most wins, ties going to the
   first result block. State lives in memory only — a reload restarts the quiz. */
(function () {
  'use strict';

  if (customElements.get('product-quiz')) return;

  class ProductQuiz extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.viewport = this.querySelector('[data-quiz-viewport]');
      this.liveRegion = this.querySelector('[data-quiz-live]');
      this.introPanel = this.querySelector('[data-quiz-panel="intro"]');
      this.resultPanel = this.querySelector('[data-quiz-panel="result"]');
      this.questionPanels = Array.from(this.querySelectorAll('[data-quiz-panel="question"]'));
      this.resultCards = Array.from(this.querySelectorAll('[data-quiz-result]'));
      this.miniItems = Array.from(this.querySelectorAll('[data-quiz-mini]'));
      this.secondary = this.querySelector('[data-quiz-secondary]');
      this.selections = this.questionPanels.map(() => null);
      this.heightTimer = null;

      if (!this.viewport || !this.introPanel || this.questionPanels.length === 0) return;

      this.querySelectorAll('[data-quiz-start]').forEach((button) => {
        button.addEventListener('click', () => this.showQuestion(0), { signal: signal });
      });

      this.questionPanels.forEach((panel, index) => {
        panel.querySelectorAll('[data-quiz-answer]').forEach((button) => {
          button.addEventListener('click', () => this.selectAnswer(index, button), { signal: signal });
        });
        const back = panel.querySelector('[data-quiz-back]');
        if (back) {
          back.addEventListener('click', () => this.goBack(index), { signal: signal });
        }
      });

      this.querySelectorAll('[data-quiz-retake]').forEach((button) => {
        button.addEventListener('click', () => this.reset(), { signal: signal });
      });

      this.scentMap = this.parseScentMap();

      /* Add-to-cart on the result card — delegated, since the card is
         server-rendered and static. */
      this.addEventListener(
        'click',
        (event) => {
          const atc = event.target.closest('[data-quiz-atc]');
          if (atc && this.contains(atc)) {
            event.preventDefault();
            this.addToCart(atc);
          }
        },
        { signal: signal }
      );

      /* Theme editor: clicking a question or result block jumps the quiz to
         the matching panel so the merchant can see what they are editing. */
      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener(
          'shopify:block:select',
          (event) => {
            if (!this.contains(event.target)) return;
            const panel = event.target.closest('[data-quiz-panel]');
            if (!panel) return;
            if (panel.dataset.quizPanel === 'result') {
              const card = event.target.closest('[data-quiz-result]');
              if (card) {
                this.resultCards.forEach((other) => {
                  const hide = other !== card;
                  if (other.hidden !== hide) other.hidden = hide;
                });
                if (this.secondary && !this.secondary.hidden) this.secondary.hidden = true;
              }
            }
            this.showPanel(panel, { skipFocus: true });
          },
          { signal: signal }
        );
      }
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      if (this.heightTimer !== null) {
        clearTimeout(this.heightTimer);
        this.heightTimer = null;
      }
    }

    selectAnswer(questionIndex, button) {
      const buttons = this.questionPanels[questionIndex].querySelectorAll('[data-quiz-answer]');
      buttons.forEach((other) => {
        const pressed = other === button ? 'true' : 'false';
        if (other.getAttribute('aria-pressed') !== pressed) other.setAttribute('aria-pressed', pressed);
      });
      this.selections[questionIndex] = button;

      if (questionIndex + 1 < this.questionPanels.length) {
        this.showQuestion(questionIndex + 1);
      } else {
        this.showResult();
      }
    }

    showQuestion(index) {
      this.showPanel(this.questionPanels[index]);
      this.announce('Question ' + (index + 1) + ' of ' + this.questionPanels.length);
    }

    goBack(index) {
      if (index === 0) {
        this.showPanel(this.introPanel);
        this.announce('Back to the start.');
      } else {
        this.showQuestion(index - 1);
      }
    }

    parseTags(value) {
      if (!value) return [];
      return value
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean);
    }

    showResult() {
      /* Tally the tags picked up along the way. */
      const counts = Object.create(null);
      this.selections.forEach((button) => {
        if (!button) return;
        this.parseTags(button.dataset.quizTags).forEach((tag) => {
          counts[tag] = (counts[tag] || 0) + 1;
        });
      });

      /* Highest overlap wins; strict > keeps ties on the first result block. */
      let winnerIndex = 0;
      let bestScore = -1;
      this.resultCards.forEach((card, index) => {
        let score = 0;
        this.parseTags(card.dataset.quizTags).forEach((tag) => {
          score += counts[tag] || 0;
        });
        if (score > bestScore) {
          bestScore = score;
          winnerIndex = index;
        }
      });

      this.resultCards.forEach((card, index) => {
        const hide = index !== winnerIndex;
        if (card.hidden !== hide) card.hidden = hide;
      });

      this.applyVariantMatch(this.resultCards[winnerIndex], counts);

      let visibleMinis = 0;
      this.miniItems.forEach((item, index) => {
        const hide = index === winnerIndex;
        if (item.hidden !== hide) item.hidden = hide;
        if (!hide) visibleMinis += 1;
      });

      if (this.secondary) {
        const hideSecondary = visibleMinis === 0;
        if (this.secondary.hidden !== hideSecondary) this.secondary.hidden = hideSecondary;
      }

      this.showPanel(this.resultPanel);
      this.announce('Quiz complete. Your recommendation is shown.');
    }

    applyVariantMatch(card, counts) {
      /* Deep-link the variant whose title matches one of the shopper's
         answer tags (e.g. scent tags terra/flora/solara), and surface
         the matched variant name on the result card. */
      if (!card || !card.dataset.quizVariants) return;
      let variants;
      try {
        variants = JSON.parse(card.dataset.quizVariants);
      } catch (error) {
        return;
      }
      const tags = Object.keys(counts);
      const match = variants.find((variant) => {
        const title = String(variant.title).toLowerCase();
        return tags.some((tag) => title.includes(tag));
      });
      if (!match) return;

      const key = String(match.title).toLowerCase().trim();
      const detail = (this.scentMap && this.scentMap[key]) || null;

      /* Add-to-cart targets the matched scent variant. */
      const atc = card.querySelector('[data-quiz-atc]');
      if (atc) atc.dataset.variantId = match.id;

      /* Scent-specific description replaces the generic one. */
      if (detail && detail.description) {
        const descEl = card.querySelector('[data-quiz-result-desc]');
        if (descEl) {
          descEl.textContent = detail.description;
          descEl.hidden = false;
        }
      }

      /* Secondary link → the scent's page, labelled with the scent. */
      const learn = card.querySelector('[data-quiz-learn-more]');
      if (learn) {
        if (detail && detail.url) learn.setAttribute('href', detail.url);
        const prefix = learn.dataset.labelPrefix || 'Learn more about';
        learn.textContent = prefix + ' ' + match.title;
      }

      const scentEl = card.querySelector('[data-quiz-result-scent]');
      if (scentEl) {
        const label = scentEl.dataset.label || 'Your scent:';
        scentEl.textContent = label + ' ' + match.title;
        scentEl.hidden = false;
      }
    }

    parseScentMap() {
      const el = this.querySelector('[data-quiz-scent-map]');
      if (!el) return {};
      try {
        return JSON.parse(el.textContent) || {};
      } catch (error) {
        return {};
      }
    }

    addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const drawer = document.querySelector('cart-drawer');
      const routes = window.routes || {};
      const addUrl = routes.cart_add_url || '/cart/add';
      const config =
        typeof window.fetchConfig === 'function'
          ? window.fetchConfig()
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : [];

      const payload = { items: [{ id: variantId, quantity: 1 }] };
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }
      config.body = JSON.stringify(payload);

      const errorEl = this.querySelector('[data-quiz-atc-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
      button.toggleAttribute('disabled', true);
      button.classList.add('is-loading');

      fetch(addUrl, config)
        .then((response) =>
          response.json().then((body) => {
            if (!response.ok || !body || body.status || body.errors) throw new Error('add-failed');
            return body;
          })
        )
        .then((state) => {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-quiz', cartData: state });
          }
          if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
            drawer.renderContents(state);
            if (typeof drawer.open === 'function') {
              try {
                drawer.open(button);
              } catch (error) {
                /* renderContents already surfaces the drawer */
              }
            }
          } else {
            window.location = routes.cart_url || '/cart';
          }
          button.toggleAttribute('disabled', false);
          button.classList.remove('is-loading');
        })
        .catch(() => {
          button.toggleAttribute('disabled', false);
          button.classList.remove('is-loading');
          if (errorEl) {
            errorEl.hidden = false;
            errorEl.textContent = 'Could not add to cart. Please try again.';
          }
        });
    }

    reset() {
      this.selections = this.questionPanels.map(() => null);
      this.querySelectorAll('[data-quiz-answer][aria-pressed="true"]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
      });
      this.showPanel(this.introPanel);
      this.announce('Quiz restarted.');
    }

    currentPanel() {
      const panels = [this.introPanel].concat(this.questionPanels);
      if (this.resultPanel) panels.push(this.resultPanel);
      return panels.find((panel) => panel && !panel.hidden) || null;
    }

    showPanel(panel, options) {
      options = options || {};
      if (!panel) return;
      const current = this.currentPanel();
      if (current === panel) {
        if (!options.skipFocus) this.focusPanel(panel);
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reduceMotion || !current) {
        if (current) current.hidden = true;
        panel.hidden = false;
      } else {
        /* Animate the wrapper's height from the old panel to the new one,
           then hand back to auto height once the transition settles. */
        const startHeight = this.viewport.offsetHeight;
        this.viewport.style.height = startHeight + 'px';
        this.viewport.classList.add('product-quiz__viewport--animating');

        current.hidden = true;
        panel.hidden = false;

        panel.classList.remove('product-quiz__panel--enter');
        void panel.offsetWidth;
        panel.classList.add('product-quiz__panel--enter');

        const endHeight = panel.offsetHeight;
        void this.viewport.offsetHeight;
        this.viewport.style.height = endHeight + 'px';

        if (this.heightTimer !== null) clearTimeout(this.heightTimer);
        this.heightTimer = setTimeout(() => {
          this.viewport.style.height = '';
          this.viewport.classList.remove('product-quiz__viewport--animating');
          this.heightTimer = null;
        }, 350);
      }

      if (!options.skipFocus) this.focusPanel(panel);
    }

    focusPanel(panel) {
      const target = panel.querySelector('[data-quiz-focus]');
      if (target) target.focus();
    }

    announce(message) {
      if (this.liveRegion && this.liveRegion.textContent !== message) {
        this.liveRegion.textContent = message;
      }
    }
  }

  customElements.define('product-quiz', ProductQuiz);
})();
